// Number presentation shared by Dashboard and Fees, mirroring the
// spark-tracker analytics conventions.

// Sats value, switching to BTC once the amount reaches 0.01 BTC.
export function fmtSats(sats: number): string {
  const btc = sats / 1e8;
  if (sats === 0) return '0';
  if (btc >= 0.01) {
    return `${btc.toLocaleString(undefined, { maximumFractionDigits: 4 })} BTC`;
  }
  return `${sats.toLocaleString()} sats`;
}

export function fmtNum(n: number): string {
  return n.toLocaleString();
}

// Compact number for chart axes: 1.2M, 4.5K, etc.
export function compact(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${Math.round(n)}`;
}

// Axis formatter for BTC values, which are usually well below 1000:
// compact() would round sub-1 ticks to "0".
export function compactBtc(n: number): string {
  if (n >= 1e3) return compact(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// USD value from a decimal string, e.g. "1234.5" -> "$1,234.50". Amounts
// that would round to $0.00 show as "< $0.01" so they don't read as nothing.
export function fmtUsd(value: string | number | null | undefined): string {
  const n = Number(value ?? 0);
  if (n > 0 && n < 0.005) return '< $0.01';
  return `$${(isNaN(n) ? 0 : n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Stablecoin amount from an integer string in the asset's smallest unit.
// Solana USDC and Spark USDB both use 6 decimals.
export function stableAmount(amount: string | null | undefined, decimals = 6): number {
  const n = Number(amount ?? 0);
  return isNaN(n) ? 0 : n / 10 ** decimals;
}

export function fmtStable(amount: string | null | undefined, asset?: string, decimals = 6): string {
  const value = stableAmount(amount, decimals).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  });
  return asset ? `${value} ${asset}` : value;
}

const CHAIN_LABELS: Record<string, string> = {
  solana: 'Solana',
  spark: 'Spark',
};

export function fmtChain(chain: string | null | undefined): string {
  if (!chain) return '';
  return CHAIN_LABELS[chain] || chain.charAt(0).toUpperCase() + chain.slice(1);
}

// "USDC on Solana"
export function fmtAssetOnChain(asset: string | null | undefined, chain: string | null | undefined): string {
  if (!asset) return '';
  return chain ? `${asset} on ${fmtChain(chain)}` : asset;
}

// Shortens a long address or hash for tables: "7EcDhS…FLtV".
export function shorten(value: string | null | undefined, head = 6, tail = 4): string {
  if (!value) return '';
  return value.length <= head + tail + 1 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;
}
